<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePortalAuthStore } from '@/stores/portalAuth'
import { usePortalNotificationsStore } from '@/stores/portalNotifications'
import { timeAgo } from '@/utils/formatters'
import { NOTIFICATION_TYPE } from '@/utils/colors'
import { portalNotificationTarget, notificationIcon } from '@/utils/notificationTarget'
import type { Notification } from '@/types/notifications'

defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const router = useRouter()
const portalAuth = usePortalAuthStore()
const store = usePortalNotificationsStore()
const dropdownRef = ref<HTMLElement | null>(null)

function handleClickOutside(e: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
    emit('close')
  }
}

function handleNotificationClick(n: Notification) {
  store.markAsRead(n.id)
  emit('close')
  const target = portalNotificationTarget(n)
  if (target) router.push(target)
}

function handleMarkAllRead() {
  store.markAllAsRead()
}

function iconColor(type: string): string {
  return NOTIFICATION_TYPE[type]?.text ?? 'text-gray-400'
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
  if (portalAuth.isAuthenticated) {
    store.fetchRecent()
  }
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})
</script>

<template>
  <Transition
    enter-active-class="transition duration-150 ease-out"
    enter-from-class="scale-95 opacity-0"
    enter-to-class="scale-100 opacity-100"
    leave-active-class="transition duration-100 ease-in"
    leave-from-class="scale-100 opacity-100"
    leave-to-class="scale-95 opacity-0"
  >
    <div
      v-if="visible"
      ref="dropdownRef"
      role="menu"
      aria-label="Notificaciones"
      class="absolute right-0 top-full z-50 mt-2 w-96 max-w-[calc(100vw-2rem)] origin-top-right rounded-xl border border-gray-200 bg-white shadow-lg"
    >
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <h3 class="text-sm font-semibold text-gray-900">
          Notificaciones
        </h3>
        <button
          v-if="store.unreadCount > 0"
          class="text-xs font-medium text-indigo-700 hover:text-indigo-800"
          @click="handleMarkAllRead"
        >
          Marcar todas como leídas
        </button>
      </div>

      <!-- Notification list -->
      <div class="max-h-96 overflow-y-auto">
        <div
          v-if="store.notifications.length === 0"
          class="px-4 py-8 text-center"
        >
          <span class="plutto-icon plutto-icon--outlined mx-auto mb-2 text-3xl text-gray-300">notifications</span>
          <p class="text-sm text-gray-500">
            No hay notificaciones
          </p>
        </div>

        <button
          v-for="n in store.notifications"
          :key="n.id"
          role="menuitem"
          :aria-label="n.body ? `${n.title} - ${n.body}` : n.title"
          :class="[
            'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50',
            !n.read_at ? 'bg-indigo-50/50' : '',
          ]"
          @click="handleNotificationClick(n)"
        >
          <span :class="['plutto-icon plutto-icon--outlined mt-0.5 text-xl shrink-0', iconColor(n.notification_type)]">{{ notificationIcon(n.notification_type) }}</span>
          <div class="min-w-0 flex-1">
            <div class="flex items-start justify-between gap-2">
              <p :class="['text-sm', !n.read_at ? 'font-semibold text-gray-900' : 'font-medium text-gray-700']">
                {{ n.title }}
              </p>
              <span
                v-if="!n.read_at"
                class="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-indigo-700"
              />
            </div>
            <p
              v-if="n.body"
              class="mt-0.5 line-clamp-2 text-xs text-gray-500"
            >
              {{ n.body }}
            </p>
            <p class="mt-1 text-xs text-gray-400">
              {{ timeAgo(n.created_at) }}
            </p>
          </div>
        </button>
      </div>
    </div>
  </Transition>
</template>
